/**
 * Le geste de désignation d'un point, **commun aux cinq graphiques** de
 * l'application *(06/09/2026)*.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 LE DÉFAUT QU'IL CORRIGE : LES GRAPHIQUES N'AVAIENT PAS DE VERROU D'AXE
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Relevé à l'usage sur l'appareil : « quand je sélectionne un point et que je
 * glisse avec mon doigt vers un autre point, la fenêtre scroll ».
 *
 * Les graphiques portaient bien `touch-action: pan-y` — « le défilement
 * vertical reste natif » —, mais **suivaient le doigt dès qu'il bougeait**,
 * sans jamais décider si le geste était un balayage ou un défilement. Or un
 * doigt qui longe une courbe ne se déplace jamais parfaitement à
 * l'horizontale : sa composante verticale suffit au navigateur pour engager un
 * défilement, que `pan-y` autorise explicitement. Les deux gestes se
 * disputaient donc le doigt à chaque balayage.
 *
 * 🔴 **La correction n'est pas un réglage, c'est le verrou d'axe que le projet
 * emploie déjà deux fois** — `useDayPager` depuis la V1 et `useSwipeToDelete`
 * depuis la refonte, tous deux à **10 px**, « deux seuils différents sur un
 * même écran se sentent au doigt ». Tant que l'axe n'est pas connu, on ne
 * désigne rien :
 *
 *   - le geste part **verticalement** → on se retire pour de bon, la page
 *     défile, et c'est exactement ce que `pan-y` promet ;
 *   - le geste part **horizontalement** → on verrouille, on capture le
 *     pointeur, et le défilement ne peut plus s'engager.
 *
 * ⚠️ **Et c'est pour ça que ça marche, alors que « empêcher le défilement »
 * après coup ne marcherait pas** : `touch-action` est évalué au **début** du
 * contact, et un `preventDefault()` arrivé après que le navigateur a engagé son
 * défilement est ignoré. On ne l'annule donc pas — on fait en sorte qu'il ne
 * commence jamais, en n'ayant pas de composante verticale à lui offrir.
 *
 * ⚠️ **Un `touchmove` non passif double la garde** une fois l'axe verrouillé.
 * Il est posé à la main (`addEventListener`, `{ passive: false }`) parce que
 * React attache ses écouteurs en passif : un `onTouchMove` de JSX ne peut pas
 * appeler `preventDefault()`.
 *
 * 🔴 **`touch-action: none` a été écarté**, bien qu'il soit la réponse courte.
 * Il rendrait impossible de faire défiler un écran en partant du graphique —
 * or l'onglet Progression est une **colonne de cartes qui sont des
 * graphiques** : il faudrait viser les rares interstices pour descendre. C'est
 * la réserve déjà consignée pour la fiche d'exercice (« elle s'ouvre en
 * feuille, donc c'est le seul moyen d'atteindre ce qui est en dessous »),
 * multipliée par cinq écrans.
 *
 * ⚠️ **La sélection se fait toujours au CONTACT**, avant tout verrouillage :
 * toucher un point le désigne immédiatement, c'est le geste de base. Le verrou
 * ne gouverne que la **suite** — savoir si l'on balaye ou si l'on s'en va.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

/**
 * Le même seuil que le pager et le glissement de suppression.
 *
 * ⚠️ **Ne pas le régler à la baisse « pour que ça réponde plus vite »** : en
 * dessous, un appui parfaitement immobile suffit à verrouiller sur un axe, et
 * un simple tap se met à voler le défilement.
 */
const AXIS_LOCK_PX = 10;

interface Geste {
  pointerId: number;
  x: number;
  y: number;
  axe: 'inconnu' | 'x' | 'abandon';
}

/**
 * @param designer appelé avec les coordonnées **relatives à l'élément**.
 *   `auContact` distingue le premier appui (qui bascule la sélection) du
 *   balayage (qui ne fait que suivre) — c'est la règle du 05/09 : « le retrait
 *   ne se fait qu'au `pointerdown`, sinon repasser sur le point sélectionné
 *   l'allumerait et l'éteindrait sous les doigts ».
 */
export function useChartSweep(designer: (x: number, y: number, auContact: boolean) => void) {
  const geste = useRef<Geste | null>(null);
  /*
   * 🔴 **Le nœud est un ÉTAT, pas une référence — et c'est un défaut évité de
   * justesse.** Avec une référence, l'effet ne serait exécuté qu'au montage,
   * `noeud.current` y vaudrait encore `null` (le SVG n'est pas rendu tant que
   * `useElementWidth` n'a pas mesuré une largeur), et l'écouteur ne serait
   * **jamais posé** — la garde non passive n'aurait servi à rien, sans que rien
   * ne le signale. En état, l'effet se rejoue quand le SVG apparaît.
   *
   * ⚠️ C'est exactement la mécanique de `useElementWidth`, qui garde son nœud
   * en `useState` pour la même raison.
   */
  const [noeud, setNoeud] = useState<SVGSVGElement | null>(null);

  /*
   * ⚠️ **Écouteur natif et non passif** — cf. l'en-tête : React attache les
   * siens en passif, donc un `onTouchMove` de JSX ne peut pas appeler
   * `preventDefault()`. Il ne bloque le défilement **que** lorsque l'axe est
   * verrouillé sur l'horizontale ; le reste du temps il ne fait rien, et la
   * page défile normalement.
   */
  useEffect(() => {
    if (noeud === null) return;

    const bloquer = (event: TouchEvent) => {
      if (geste.current?.axe === 'x' && event.cancelable) event.preventDefault();
    };
    noeud.addEventListener('touchmove', bloquer, { passive: false });
    return () => {
      noeud.removeEventListener('touchmove', bloquer);
    };
  }, [noeud]);

  /*
   * ⚠️ **Nommée `attacher` et non `ref`** : `react-hooks/refs` refuse
   * `sweep.ref` en JSX — il y lit l'accès à un objet de référence pendant le
   * rendu, ce qu'il interdit à juste titre. C'en est une **fonction de
   * rappel**, pas un objet, mais le nom suffisait à déclencher la règle. La
   * renommer coûte moins qu'une exception, et dit mieux ce qu'elle fait.
   */
  const attacher = useCallback((element: SVGSVGElement | null) => {
    setNoeud(element);
  }, []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      const boite = event.currentTarget.getBoundingClientRect();
      geste.current = {
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        axe: 'inconnu',
      };
      designer(event.clientX - boite.left, event.clientY - boite.top, true);
    },
    [designer],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      const g = geste.current;
      if (g === null || g.pointerId !== event.pointerId || g.axe === 'abandon') return;

      const dx = event.clientX - g.x;
      const dy = event.clientY - g.y;

      if (g.axe === 'inconnu') {
        // Tant qu'on ne sait pas, on ne désigne rien et on ne bloque rien.
        if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
        if (Math.abs(dy) >= Math.abs(dx)) {
          /*
           * 🔴 **Défilement : on se retire pour de bon.** Pas « on met en
           * pause » — le geste entier appartient désormais à la page, et
           * reprendre la main au milieu ferait sauter la sélection pendant
           * qu'on descend l'écran.
           */
          g.axe = 'abandon';
          return;
        }
        g.axe = 'x';
        /*
         * ⚠️ **Capture du pointeur**, comme le pager : le doigt peut sortir du
         * cadre par le haut ou par le bas sans casser le balayage. Sans elle,
         * longer une courbe qui monte perdrait le suivi à mi-chemin.
         * ⚠️ Sans danger ici, contrairement au glissement de suppression : un
         * graphique ne contient aucun bouton dont la capture retargetterait le
         * `click`.
         */
        event.currentTarget.setPointerCapture(event.pointerId);
      }

      const boite = event.currentTarget.getBoundingClientRect();
      designer(event.clientX - boite.left, event.clientY - boite.top, false);
    },
    [designer],
  );

  const finir = useCallback(() => {
    geste.current = null;
  }, []);

  return {
    attacher,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: finir,
      onPointerCancel: finir,
    },
  };
}
